import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";

import { UrlConstantes } from "../utilerias/constantes/urlconstantes";
import {
  GocePermiso,
  Permiso,
  PermisoReq,
  PermisoRes,
} from "../shared/model/permisos/permiso.model";

/**
 * Los permisos del FRC-017. Servicio aparte del de vacaciones: son otro
 * módulo, otra tabla y otros endpoints, aunque compartan pantalla.
 */
@Injectable({
  providedIn: "root",
})
export class PermisosService {
  constructor(private http: HttpClient) {}

  /**
   * Todo de una vez: los permisos propios, los que le toca resolver y el
   * encabezado del formato propuesto desde el organigrama. Van juntos
   * para pintar la pestaña con una sola llamada.
   */
  consultar(desde?: string, hasta?: string): Observable<PermisoRes> {
    const req: PermisoReq = { desde, hasta };
    return this.http.post<PermisoRes>(UrlConstantes.PERMISOS_CONSULTAR, req);
  }

  /**
   * Sólo los que esperan respuesta de quien pregunta. Es lo que necesita
   * un contador o la lista de pendientes; consultar() trae además lo
   * propio, el encabezado y los jefes, que para eso sobran.
   */
  porResolver(): Observable<PermisoRes> {
    const req: PermisoReq = {};
    return this.http.post<PermisoRes>(UrlConstantes.PERMISOS_POR_RESOLVER, req);
  }

  solicitar(permiso: Permiso): Observable<PermisoRes> {
    const req: PermisoReq = { permiso };
    return this.http.post<PermisoRes>(UrlConstantes.PERMISOS_SOLICITAR, req);
  }

  /** El `goce` sólo viaja al autorizar: es la decisión de quien resuelve. */
  resolver(
    idPermiso: number,
    aprobar: boolean,
    motivo?: string,
    firma?: string,
    goce?: GocePermiso,
  ): Observable<PermisoRes> {
    const req: PermisoReq = { idPermiso, aprobar, motivo, firma, goce };
    return this.http.post<PermisoRes>(UrlConstantes.PERMISOS_RESOLVER, req);
  }

  cancelar(idPermiso: number): Observable<PermisoRes> {
    const req: PermisoReq = { idPermiso };
    return this.http.post<PermisoRes>(UrlConstantes.PERMISOS_CANCELAR, req);
  }

  /** Archiva un permiso retirado (deja de salir en «Tus permisos») o, con archivar en false, lo saca del archivo. */
  archivar(idPermiso: number, archivar = true): Observable<PermisoRes> {
    const req: PermisoReq = { idPermiso, archivar };
    return this.http.post<PermisoRes>(UrlConstantes.PERMISOS_ARCHIVAR, req);
  }

  /**
   * El calendario: los permisos de todos en el rango. Sólo lo puede
   * pedir quien administra el calendario; el servidor lo comprueba.
   *
   * Con `todo` en verdadero devuelve la BITÁCORA: los mismos días pero
   * con todos los estatus, rechazados y retirados incluidos, del más
   * reciente al más viejo. El calendario pinta lo que va a pasar ese
   * día; la bitácora registra lo que se pidió y en qué paró.
   */
  calendario(desde: string, hasta: string, todo = false): Observable<PermisoRes> {
    const req: PermisoReq = { desde, hasta, todo };
    return this.http.post<PermisoRes>(UrlConstantes.PERMISOS_CALENDARIO, req);
  }

  /**
   * Los permisos de la gente que autoriza quien pregunta, pendientes y
   * autorizados, del día `desde` en adelante: los pinta el calendario «Tu
   * equipo» de «Mi equipo» (29/10/2026). El servidor decide quién es su
   * gente, con la misma regla que sus vacaciones.
   */
  equipo(desde: string): Observable<PermisoRes> {
    const req: PermisoReq = { desde };
    return this.http.post<PermisoRes>(UrlConstantes.PERMISOS_EQUIPO, req);
  }

  /** Los datos del FRC-017 con las firmas. El PDF se dibuja en el navegador. */
  frc(idPermiso: number): Observable<PermisoRes> {
    const req: PermisoReq = { idPermiso };
    return this.http.post<PermisoRes>(UrlConstantes.PERMISOS_FRC, req);
  }

  /** Sube el FRC-017 firmado a mano (PDF, JPG o PNG). */
  subirFirmado(idPermiso: number, archivo: File): Observable<PermisoRes> {
    const forma = new FormData();
    forma.append("file", archivo, archivo.name);
    forma.append("idPermiso", String(idPermiso));
    return this.http.post<PermisoRes>(UrlConstantes.PERMISOS_FIRMADO_SUBIR, forma);
  }

  /** Los bytes del FRC-017 firmado, por HttpClient para que viaje el token. */
  verFirmado(idPermiso: number, idFirmado?: number): Observable<Blob> {
    const req: PermisoReq = { idPermiso, idFirmado };
    return this.http.post(UrlConstantes.PERMISOS_FIRMADO_VER, req, { responseType: "blob" });
  }

  /** Los FRC-017 firmados que se han subido al permiso, del último al primero. */
  historialFirmado(idPermiso: number): Observable<PermisoRes> {
    const req: PermisoReq = { idPermiso };
    return this.http.post<PermisoRes>(UrlConstantes.PERMISOS_FIRMADO_HISTORIAL, req);
  }
}
